/**
 * Hooks React Query para a Base de Conhecimento (KB) de uma API Key do WhatsApi.
 *
 * `raiz` é a raiz das rotas do escopo (tenant ou organização, ver `whatsapiKbApi`); entra na
 * chave do cache, então o KB de uma chave nunca aparece no escopo de outra.
 *
 * Polling: useKbFiles usa refetchInterval que retorna 2500ms enquanto houver
 * algum arquivo em pending/processing, senão false (encerra o polling). Sem
 * teto manual — o polling para naturalmente em ready/failed. 429 é tratado no
 * consumidor (retry: false + notificação).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { whatsapiKbApi, type KbFile } from '@/api/whatsapi'

const KEY = (raiz: string, keyId: string) => ['whatsapi-kb', raiz, keyId] as const

export function useKbFiles(raiz: string | undefined, keyId: string | null, enabled = true) {
  return useQuery<KbFile[]>({
    queryKey: KEY(raiz ?? '', keyId ?? ''),
    queryFn: () => whatsapiKbApi.list(raiz as string, keyId as string),
    enabled: !!raiz && !!keyId && enabled,
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data as KbFile[] | undefined
      const busy = data?.some((f) => f.status === 'pending' || f.status === 'processing')
      return busy ? 2500 : false
    },
  })
}

function useInvalidate(raiz: string, keyId: string) {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: KEY(raiz, keyId) })
}

export function useKbUpload(raiz: string, keyId: string) {
  const invalidate = useInvalidate(raiz, keyId)
  return useMutation({
    mutationFn: (file: File) => whatsapiKbApi.upload(raiz, keyId, file),
    onSuccess: invalidate,
  })
}

export function useKbToggle(raiz: string, keyId: string) {
  const invalidate = useInvalidate(raiz, keyId)
  return useMutation({
    mutationFn: ({ fileId, isActive }: { fileId: string; isActive: boolean }) =>
      whatsapiKbApi.setActive(raiz, keyId, fileId, isActive),
    onSuccess: invalidate,
  })
}

export function useKbDelete(raiz: string, keyId: string) {
  const invalidate = useInvalidate(raiz, keyId)
  return useMutation({
    mutationFn: (fileId: string) => whatsapiKbApi.remove(raiz, keyId, fileId),
    onSuccess: invalidate,
  })
}

export function useKbReprocess(raiz: string, keyId: string) {
  const invalidate = useInvalidate(raiz, keyId)
  return useMutation({
    mutationFn: (fileId: string) => whatsapiKbApi.reprocess(raiz, keyId, fileId),
    onSuccess: invalidate,
  })
}

export function useKbContextPreview(raiz: string, keyId: string) {
  return useMutation({
    mutationFn: (q: string) => whatsapiKbApi.context(raiz, keyId, q),
  })
}
